<template>
  <b-modal
    id="group-wizard-modal"
    v-model="isWizardActive"
    title="Group Wizard"
    hide-header
    centered
    no-close-on-esc
    no-close-on-backdrop
    content-class="modal-user"
    :size="modalSize"
    @shown="onShown"
    @hidden="resetAll"
  >
    <h4 class="mb-1 text-capitalize text-center py-1 title-user-wizard">
      {{ (isAdd ? $t("actions.add") : $t("actions.edit")) + " " + $tc("group.title", 1) }}
    </h4>

    <WizardStepIndicator
      :currentStep="currentStep"
      :numberedSteps="numberedSteps"
      :stepIcons="stepIcons"
      @step-clicked="handleStepClicked"
      :type-alarm-options="typeAlarmOptions"
      :is-add.sync="isAdd"
    />

    <hr />

    <div class="b-modal-body" :style="modalStyle">
      <div v-if="loading" class="modal-loading">
        <b-spinner
          v-if="currentStep === 2 || loading"
          label="Spinning"
          variant="primary"
          class="modal-loading-spinning"
        ></b-spinner>
      </div>

      <GeneralInfoStep
        v-if="currentStepKey === 'general' && !loading"
        ref="generalInfoStepRef"
        :is-add.sync="isAdd"
        :prop-item-data="itemData"
        :initial-user-labels="userLabelsForStep"
        @update:itemData="itemData = { ...itemData, ...$event }"
      />

      <TenantStep
        v-if="currentStepKey === 'tenants' && !loading"
        ref="tenantStepRef"
        :is-add.sync="isAdd"
        :prop-item-data="itemData"
        :current-step="currentStep"
        :tenant-options="tenantOptions"
        :type-alarm-options="typeAlarmOptions"
        @update:itemData="itemData = { ...itemData, ...$event }"
      />

      <PermissionStep
        v-if="currentStepKey === 'profile' && !loading"
        ref="permissionStepRef"
        :prop-item-data="itemData"
        :profile-options="profileOptions"
        @update:itemData="itemData = { ...itemData, ...$event }"
      />

      <Device
        v-if="currentStepKey === 'devices'"
        :config="config"
        @updateStatus="updateStatus"
        :assingDevices="assingDevices"
        :is-parent-loading="parentLoading"
      />
    </div>

    <template #modal-footer>
      <div class="mr-auto">
        <button
          type="button"
          class="btn btn-danger text-capitalize"
          :disabled="loading"
          @click="closeModal"
        >
          {{ $t("actions.cancel") }}
        </button>
      </div>
      <div class="d-flex justify-content-between">
        <div class="d-flex">
          <button
            type="button"
            data-cy="automatest-wizard-save-btn"
            class="btn btn-primary mr-2 text-capitalize"
            @click="save"
            :disabled="saveDisabled || loading"
            v-if="!isAdd && currentStepKey !== 'devices'"
          >
            {{ $t("actions.save_changes") }}
          </button>
          <button
            type="button"
            class="btn btn-secondary mr-2 text-capitalize"
            @click="currentStep--"
            :disabled="currentStep === 0 || loading"
          >
            {{ $t("actions.previous") }}
          </button>
          <button
            type="button"
            data-cy="automatest-wizard-next-btn"
            class="btn btn-primary text-capitalize"
            @click="nextStep"
            :disabled="loading"
            v-show="!(currentStep === numberedSteps.length - 1) && !isLastGroupStep"
          >
            {{ $t("actions.next") }}
          </button>
          <button
            type="button"
            data-cy="automatest-wizard-finish-btn"
            class="btn btn-primary text-capitalize"
            @click="closeModal"
            v-if="currentStep === numberedSteps.length - 1 || isLastGroupStep"
            :disabled="loading"
          >
            {{ $t("actions.finish") }}
          </button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { BModal, BSpinner } from "bootstrap-vue";
import WizardStepIndicator from "./WizardStepIndicator.vue";
import GeneralInfoStep from "./GeneralInfoStep.vue";
import TenantStep from "./TenantStep.vue";
import PermissionStep from "./PermissionStep.vue";
import CamerasStep from "@/views/user_cameras/Index.vue";
import store from "@/store";
import axios from "@axios";
import { ref, computed, watch, nextTick, onMounted } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import Device from "@/components/DeviceAssignment/Index.vue";
import ability from "@/libs/acl/ability";

export default {
  components: {
    BModal,
    BSpinner,
    WizardStepIndicator,
    TenantStep,
    GeneralInfoStep,
    PermissionStep,
    CamerasStep,
    Device,
  },
  props: {
    isWizardActive: {
      type: Boolean,
      required: true,
    },
    typeAlarmOptions: {
      type: Array,
      required: true,
    },
    tenantOptions: {
      type: Array,
      required: true,
    },
    profileOptions: {
      type: Array,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
  },
  setup(props, { emit, parent }) {
    const currentStep = ref(0);
    const showAllUserSteps = ref(false);
    const userBelongGroup = ref(false);
    const activeSteps = computed(() => [
      {
        key: "general",
        label: parent.$i18n.t("users.info"),
        icon: "UserIcon",
        visible: showAllUserSteps.value || ability.can("update_general_info", "Group"),
      },
      {
        key: "tenants",
        label: parent.$i18n.tc("tenants.title", 2),
        icon: "UsersIcon",
        visible: showAllUserSteps.value || ability.can("update_tenants", "Group"),
      },
      {
        key: "profile",
        label: parent.$i18n.tc("permissions.title", 2),
        icon: "CheckCircleIcon",
        visible: showAllUserSteps.value || ability.can("update_profile", "Group"),
      },
      {
        key: "devices",
        label: parent.$i18n.tc("camera.title", 2),
        icon: "VideoIcon",
        visible: showAllUserSteps.value || ability.can("update_cameras", "Group"),
      },
    ].filter(step => step.visible));
    const numberedSteps = computed(() => activeSteps.value.map(step => step.label));
    const stepIcons = computed(() => activeSteps.value.map(step => step.icon));
    const currentStepKey = computed(() => activeSteps.value[currentStep.value]?.key);
    const isLastGroupStep = computed(() =>
      userBelongGroup.value && currentStep.value === activeSteps.value.length - 2
    );
    const loading = ref(false);
    const generalInfoStepRef = ref(null);
    const tenantStepRef = ref(null);
    const permissionStepRef = ref(null);
    const userLabelsForStep = ref([]);

    //Variables to use later in camera assign
    const groupId = ref(null);
    const groupName = ref(null);

    const saveDisabled = ref("true");
    const firstItemDataOnEdit = ref(null);
    const isInitialLoadComplete = ref(false);
    const parentLoading = ref(false);

    const blankData = {
      name: "",
      users: [],
      tenant: [],
      permission: null,
      type_alarm: null,
      frame_color: "#FF0000",
      check_alarms: false,
      id_profile: null,
    };

    const toast = useToast();
    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    };

    const loadData = () => {
      return new Promise((resolve) => {
        loading.value = true;
        if (!props.isAdd) {
          itemData.value = JSON.parse(JSON.stringify(props.itemEdit));
          const rawUsers = props.itemEdit.users || [];
          const userIds = rawUsers.map((user) =>
            typeof user === "object" && user != null && user.id != null ? user.id : user
          );
          itemData.value.users = [...new Set(userIds)];
          itemData.value.tenant = props.itemEdit.tenants.map((tenant) => tenant.id);
          itemData.value.permission = props.itemEdit.permissions.map(
            (permission) => permission.id
          );
          itemData.value.type_alarm = props.itemEdit.types_alarms.map(
            (type_alarm) => type_alarm.id
          );
          if (!props.itemEdit.frame_color) {
            itemData.value.frame_color = "#FF0000";
          }
          groupId.value = props.itemEdit.id;
          groupName.value = props.itemEdit.name;
          userLabelsForStep.value = rawUsers.filter(
            (u) => u && typeof u === "object" && u.id != null && (u.email != null || u.label != null)
          );
          firstItemDataOnEdit.value = JSON.parse(JSON.stringify(itemData.value));
          isInitialLoadComplete.value = true;
        }

        loading.value = false;
        setTimeout(setUpWatch, 5000);
        resolve();
        // store.dispatch("user/getAndSetAllUsersWithoutGroup").then(() => {
        //   loading.value = false;
        //   userOptions.value = store.getters["user/getUsersWithOutGroupSelectItems"];

        //   if (!props.isAdd) {
        //     itemData.value = JSON.parse(JSON.stringify(props.itemEdit));

        //     itemData.value.users = props.itemEdit.users.map((user) => {
        //       return user.id;
        //     });

        //     itemData.value.tenant = props.itemEdit.tenants.map((tenant) => {
        //       return tenant.id;
        //     });

        //     itemData.value.permission = props.itemEdit.permissions.map((permission) => {
        //       return permission.id;
        //     });

        //     itemData.value.type_alarm = props.itemEdit.types_alarms.map((type_alarm) => {
        //       return type_alarm.id;
        //     });

        //     //add current group users to edit options
        //     const editedUsers = props.itemEdit.users.map((user) => ({
        //       value: user.id,
        //       label: user.email,
        //     }));

        //     userOptions.value = [...userOptions.value, ...editedUsers];

        //     if (!props.itemEdit.frame_color) {
        //       itemData.value.frame_color = "#FF0000";
        //     }

        //     groupId.value = props.itemEdit.id;
        //     groupName.value = props.itemEdit.name;

        //     firstItemDataOnEdit.value = itemData.value;
        //     isInitialLoadComplete.value = true;
        //   }

        //   loading.value = false;
        //   setTimeout(setUpWatch, 5000);
        //   resolve();
        // });
      });
    };

    const setUpWatch = () => {
      watch(
        () => JSON.parse(JSON.stringify(itemData.value)),
        (newVal, oldVal) => {
          if (isInitialLoadComplete.value) {
            const original = JSON.stringify(firstItemDataOnEdit.value);
            const updated = JSON.stringify(newVal);
            if (original !== updated) {
              saveDisabled.value = false;
            } else {
              saveDisabled.value = true;
            }
          }
        },
        { deep: true }
      );
    };

    const nextStep = async () => {
      let isValid = true;
      if (
        currentStepKey.value === "general" &&
        generalInfoStepRef.value &&
        generalInfoStepRef.value.validateStep
      ) {
        isValid = await generalInfoStepRef.value.validateStep();
      } else if (
        currentStepKey.value === "tenants" &&
        tenantStepRef.value &&
        tenantStepRef.value.validateStep
      ) {
        isValid = await tenantStepRef.value.validateStep();
      } else if (
        currentStepKey.value === "profile" &&
        permissionStepRef.value &&
        permissionStepRef.value.validateStep
      ) {
        isValid = true; //await permissionStepRef.value.validateStep(); //TODO: validar en el componente si tiene permisos o no.
      }

      const nextStepData = activeSteps.value[currentStep.value + 1];
      if (isValid && nextStepData?.key === "devices") {
        if (props.isAdd) {
          const result = await submit();
          loading.value = false;
          if (!result) return; //something went wrong we dont change to cameras step on new users.
        }

        //get cameras for the next step
        loading.value = true;
        let params = {
          group: groupId.value,
          assign: 1,
          action: "justIds",
        };

        await store.dispatch("camera_proxy/getAll", params);
        loading.value = false;

        currentStep.value++;
        return;
      }

      if (isValid) {
        currentStep.value++;
      }
    };

    const handleStepClicked = async (step) => {
      if (activeSteps.value[step]?.key === "devices") {
        //if its the camerasStep

        loading.value = true;
        let params = {
          group: groupId.value,
          assign: 1,
          action: "justIds",
        };

        await store.dispatch("camera_proxy/getAll", params);
        loading.value = false;
      }

      currentStep.value = step;
    };

    const onShown = () => {
      loadData();
      showAllUserSteps.value = props.isAdd;
      currentStep.value = 0;
    };

    const save = async () => {
      let generalInfoStepValid = true;
      let tenantsStepValid = true;
      let permisionStepValid = true;

      if (generalInfoStepRef.value) {
        generalInfoStepValid = await generalInfoStepRef.value.validateStep();
      }

      if (tenantStepRef.value) {
        tenantsStepValid = await tenantStepRef.value.validateStep();
      }

      if (permissionStepRef.value) {
        permisionStepValid = await permissionStepRef.value.validateStep();
      }

      if (generalInfoStepValid && tenantsStepValid && permisionStepValid) {
        submit();
      }
    };

    //Used this logic and not the store to catch the 400 bad request errors.
    const submit = async () => {
      loading.value = true;

      toast({
        component: ToastificationContent,
        props: {
          title: parent.$i18n.t("users.saving_info"),
          icon: "InfoIcon",
          variant: "info",
        },
      });

      //TEMPORAL: to not send permissions on save is add
      const data = { ...itemData.value };
      delete data.permission;

      try {
        const response = props.isAdd
          ? await axios.post("v1/groups", data, {
              validateStatus: (status) => status >= 200 && status < 500,
            })
          : await axios.put(
              `v1/groups/${itemData.value.id}`,
              {
                name: itemData.value.name,
                users: itemData.value.users,
                tenant: itemData.value.tenant,
                id_profile: itemData.value.id_profile,
                type_alarm: itemData.value.type_alarm,
                frame_color: itemData.value.frame_color,
                check_alarms: itemData.value.check_alarms,
              },
              {
                validateStatus: (status) => status >= 200 && status < 500,
              }
            );

        if (response.status >= 200 && response.status < 300) {
            toast({
                component: ToastificationContent,
                props: {
                    title: response.data.message,
                    icon: "CheckIcon",
                    variant: "success",
                },
            });
            if (props.isAdd) {
                groupId.value = response.data.data.id;
                groupName.value = response.data.data.name;
                itemData.value.id = response.data.data.id;
                emit("isAdd-false");
            }
            if (!props.isAdd && groupId.value) {
              const res = await store.dispatch("group/show", groupId.value);
              const group = res.data != null ? res.data : res;
              const rawUsers = (group && group.users) || [];
              userLabelsForStep.value = rawUsers.filter(
                (u) =>
                  u &&
                  typeof u === "object" &&
                  u.id != null &&
                  (u.email != null || u.label != null)
              );
            }
            emit("refetch-data");
            loading.value = false;
            return true;
        } else if (response.status === 422 || response.status === 400) {
            let string = "";
            Object.entries(response.data.data).forEach(([key, value]) => {
                string += `${key} - ${value}<br>`;
            });
            toast({
                component: ToastificationContent,
                props: {
                    title: response.data.message,
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                    text: string,
                },
            });
            loading.value = false;
            return false;
        } else {
            toast({
                component: ToastificationContent,
                props: {
                    title: response.data.message || "Error",
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                },
            });
            loading.value = false;
            return false;
        }
    } catch (error) {
        if (response.status === 422 || response.status === 400) {
          let string = "";
          Object.entries(response.data.data).forEach(([key, value]) => {
            string += `${key} - ${value}<br>`;
          });
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
              text: string,
            },
          });
          loading.value = false;
          return false;
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || "Error",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          loading.value = false;
          return false;
        }
      }
    };

    const closeModal = () => {
      emit("close-wizard");
    };

    const resetAll = () => {
      resetData();
      userLabelsForStep.value = [];
      currentStep.value = 0;
    };

    //CameraAssignment
    const config = {
      //id_omniview_reseller: null,
      //id_user: computed(() => userId.value),
      id_group: computed(() => groupId.value),
      filters: {
        tenant: true,
        center: true,
        sector: true,
        units: true,
        online: false,
        assing: false,
        types: true,
      },
    };

    const assingDevices = computed(
      () => store.getters["camera_proxy/getAssignedIdsItems"]
    );

    const updateStatus = async (data) => {
      parentLoading.value = true;
      if (data.multiple) {
        //if it is multiple with the options below
        if (!data.ids) {
          //if the component send the ids that want to assign, if the component do no send the ids we get all the ids depending on the filters
          let params_get_all = {
            //todo: ver si mandar los campos faltantes para que se mande todo lo que se ve en la vista.
            q: data.searchQuery,
            // max: data.perPage,
            //"page[n]": currentPage.value,
            // sortBy: data.sortBy,
            units: data.id_unit,
            id_sector: data.id_sector,
            id_center: data.id_center,
            device_type: data.id_type,
            online: data.online,
            tenant: data.id_tenant,
            action: "justIds",
            //id_user: userId.value,
            group: groupId.value,
          };

          const response = await store.dispatch("camera_proxy/getAll", params_get_all);

          data.ids = response.data.cameraProxyIds;
        }
      }

      //function to assign
      let params = {
        id_group: groupId.value,
        cameraProxyIds: data.ids ? data.ids : [data.id], //if it is not multiple and its just 1 camera
        status: data.status,
      };

      store
        .dispatch("group/assignCameras", params)
        .then((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.data,
              icon: "CheckIcon",
              variant: "success",
            },
          });
          (async () => {
            try {
              await store.dispatch("camera_proxy/getAll", {
                group: groupId.value,
                action: "justIds",
                assign: 1,
              });
              parentLoading.value = false;
            } catch (error) {
              parentLoading.value = false;
              console.error("Error executing action:", error);
            }
          })();
        })
        .catch((err) => {
          parentLoading.value = false;
          console.log(err);
        });
    };

    const modalSize = computed(() => {
      return currentStepKey.value === "devices" ? "xl" : "lg";
    });

    const modalStyle = computed(() => {
      return {
        height: "60vh",
        overflowY: "auto",
      };
    });

    return {
      currentStep,
      numberedSteps,
      stepIcons,
      currentStepKey,
      isLastGroupStep,
      itemData,
      groupName,
      generalInfoStepRef,
      tenantStepRef,
      permissionStepRef,
      userLabelsForStep,
      loadData,
      resetData,
      nextStep,
      handleStepClicked,
      submit,
      loading,
      closeModal,
      resetAll,
      config,
      save,
      saveDisabled,
      parentLoading,
      onShown,
      modalSize,
      modalStyle,
      assingDevices,
      updateStatus,
    };
  },
};
</script>

<style scoped>
.close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 1.5rem;
  cursor: pointer;
}

.div-container {
  max-width: 80%;
  margin: auto;
}
</style>

<style scoped>
.modal-user .modal-body {
  padding: 0 !important;
}
</style>
